import { Location } from '@angular/common';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { environment } from '../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { RuntimeEnvironments } from '@models/runtime-enviroment.enum';
import { AuthService } from '@service/auth.service';
import { ThemeService } from '@service/theme.service';
import { EnvironmentService } from '@service/environment.service';
import { ContactService } from '@service/contact.service';
import { ContactFormComponent } from '@components/contact-form/contact-form.component';
import { SettingsService } from '@service/settings.service';
import { AccessRequestService } from '@service/access-request.service';
import { LanguageService } from '@service/language.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    title = 'icreate client';
    location: Location;
    public env: RuntimeEnvironments;
    canDisplay = false;
    isIframe = false;
    showLandingPage = false;
    private readonly _destroying$ = new Subject<void>();

    pagesAreLoading: boolean = false;
    loadingSubscription: Subscription;

    @HostListener('window:beforeunload', [])
    async beforeUnload() {
        const accessRequestId = this.accessRequestService.getCurrentAccessRequestId();
        if (accessRequestId) {
            this.accessRequestService.setCurrentAccessRequestId(undefined);
            this.accessRequestService.sendStopEditingBeacon(accessRequestId);
        }
    }

    constructor(
        private router: Router,
        private environmentService: EnvironmentService,
        private settingsService: SettingsService,
        private themeservice: ThemeService,
        private authService: AuthService,
        private contactService: ContactService,
        private dialog: MatDialog,
        location: Location,
        private iconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private accessRequestService: AccessRequestService,
        private languageService: LanguageService
    ) {
        this.location = location;
        this.env = this.environmentService.getEnvironment();

        let pathIcons = '../assets/icons/';

        if (this.env === RuntimeEnvironments.ELECTRON) {
            pathIcons = 'assets/icons/';
        }

        const icons: [string, string][] = [
            //sidebar
            ['recents', pathIcons + 'recents.svg'],
            ['folder', pathIcons + 'folder.svg'],
            ['arrow-down', pathIcons + 'arrow-down.svg'],
            ['file', pathIcons + 'file.svg'],
            ['settings', pathIcons + 'settings.svg'],
            ['vessel-folder', pathIcons + 'vessel-folder.svg'],
            ['lock-profile', pathIcons + 'lock-and-profile.svg'],
            ['profile-picture', pathIcons + 'user-profile-picture.svg'],
            ['profile-cross', pathIcons + 'user-profile-and-cross.svg'],
            ['search', pathIcons + 'search.svg'],
            ['logout', pathIcons + 'logout.svg'],
            ['devices', pathIcons + 'devices.svg'],
            ['network-settings', pathIcons + 'network-settings.svg'],

            ['breadcrumb-chevron', pathIcons + 'breadcrumb-chevron.svg'],

            //shipyard
            ['list', pathIcons + 'list.svg'],
            ['grid', pathIcons + 'grid.svg'],
            ['vessel', pathIcons + 'vessel.svg'],
            //vessel
            ['plus', pathIcons + 'plus-symbol.svg'],
            ['minus', pathIcons + 'minus-symbol.svg'],
            ['approval', pathIcons + 'approval.svg'],
            ['unlocked', pathIcons + 'unlocked.svg'],
            ['lock', pathIcons + 'lock.svg'],
            ['fcu', pathIcons + 'fcu.svg'],
            ['mimics', pathIcons + 'mimics.svg'],
            ['ops-screen', pathIcons + 'ops-screen.svg'],
            ['download', pathIcons + 'download.svg'],
            ['graphs', pathIcons + 'graphs.svg'],
            ['excel-channels', pathIcons + 'excel-channels.svg'],
            ['nexus', pathIcons + 'nexus.svg'],
            ['import', pathIcons + 'import.svg'],
            ['fcu', pathIcons + 'fcu.svg'],
            ['cloud', pathIcons + 'cloud.svg'],
            ['pending-approval', pathIcons + 'pending-approval.svg'],
            ['device-list', pathIcons + 'device-list.svg'],
            ['vessel-page-mimics', pathIcons + 'vessel-page-mimics.svg'],
            ['vessel-page-data', pathIcons + 'vessel-page-data.svg'],
            ['vessel-page-excel', pathIcons + 'vessel-page-excel.svg'],
            ['vessel-page-nexus', pathIcons + 'vessel-page-nexus.svg'],
            ['vessel-page-print', pathIcons + 'vessel-page-print.svg'],
            ['vessel-page-spec', pathIcons + 'vessel-page-spec.svg'],

            ['more-options', pathIcons + 'more-options.svg'],
            ['edit-pen', pathIcons + 'edit-pen.svg'],
            ['delete', pathIcons + 'delete.svg'],
            ['duplicate', pathIcons + 'duplicate.svg'],
            ['lock-clock', pathIcons + 'lock-clock.svg'],
            ['info', pathIcons + 'info.svg'],
            ['question-mark', pathIcons + 'question-mark.svg'],
            ['filter-icon', pathIcons + 'filter-icon.svg'],
            ['warning', pathIcons + 'warning.svg'],
            ['check-circle', pathIcons + 'active-check.svg'],
            ['empty-box', pathIcons + 'empty-box.svg'],

            //versions-history
            ['spaceship', pathIcons + 'spaceship.svg'],
            ['flag', pathIcons + 'flag.svg'],
            ['arrow-right', pathIcons + 'arrow-right.svg'],

            //channels-page
            ['add-box', pathIcons + 'add-box.svg'],
            ['export', pathIcons + 'export.svg'],
            ['help', pathIcons + 'help.svg'],
            ['keyboard-double-arrow-left', pathIcons + 'keyboard-double-arrow-left.svg'],
            ['keyboard-double-arrow-right', pathIcons + 'keyboard-double-arrow-right.svg'],
            ['visible', pathIcons + 'visible.svg'],
            ['invisible', pathIcons + 'invisible.svg'],
            ['dropdown-arrow', pathIcons + 'dropdown-arrow.svg'],
            ['swap', pathIcons + 'swap.svg'],

            // editor
            ['redo', pathIcons + 'redo.svg'],
            ['undo', pathIcons + 'undo.svg'],
            ['loading-save', pathIcons + 'loading-save.svg'],
            ['save-error', pathIcons + 'save-error.svg'],
            ['tick', pathIcons + 'tick.svg'],

            ['arrow-circle', pathIcons + 'arrow-circle.svg'],
            ['zoom-in', pathIcons + 'zoom-in.svg'],
            ['zoom-out', pathIcons + 'zoom-out.svg'],
            ['preview', pathIcons + 'preview.svg'],
            ['text', pathIcons + 'text.svg'],
            ['connect', pathIcons + 'connect.svg'],
            ['draw-pan', pathIcons + 'draw-pan.svg'],
            ['control', pathIcons + 'control.svg'],
            ['shape', pathIcons + 'shape.svg'],
            ['pan', pathIcons + 'pan.svg'],
            ['print', pathIcons + 'print.svg'],

            ['vector', pathIcons + 'vector.svg'],
            ['free-draw', pathIcons + 'free-draw.svg'],

            ['input-value', pathIcons + 'input-value.svg'],
            ['output-value', pathIcons + 'output-value.svg'],
            ['bar', pathIcons + 'bar.svg'],
            ['button', pathIcons + 'button.svg'],
            ['status', pathIcons + 'status.svg'],
            ['toggle-switch', pathIcons + 'toggle-switch.svg'],
            ['valve', pathIcons + 'valve.svg'],
            ['pump', pathIcons + 'pump.svg'],
            ['gauge', pathIcons + 'gauge.svg'],
            ['gauge-default', pathIcons + 'gauge-object.svg'],

            ['rectangle', pathIcons + 'rectangle.svg'],
            ['ellipse', pathIcons + 'ellipse.svg'],
            ['line', pathIcons + 'line.svg'],
            ['arrow', pathIcons + 'arrow.svg'],
            ['add-image', pathIcons + 'add-image.svg'],
            ['add-svg', pathIcons + 'add-svg.svg'],
            ['polygon', pathIcons + 'polygon.svg'],
            ['triangle', pathIcons + 'triangle.svg'],

            ['move', pathIcons + 'move.svg'],
            ['grab', pathIcons + 'grab.svg'],
            ['group', pathIcons + 'group.svg'],
            ['ungroup', pathIcons + 'ungroup.svg'],
            ['editor-duplicate', pathIcons + 'editor-duplicate.svg'],

            ['align-left', pathIcons + 'align-left.svg'],
            ['align-center-horizontal', pathIcons + 'align-center-horizontal.svg'],
            ['align-right', pathIcons + 'align-right.svg'],
            ['align-top', pathIcons + 'align-top.svg'],
            ['align-center-vertical', pathIcons + 'align-center-vertical.svg'],
            ['align-bottom', pathIcons + 'align-bottom.svg'],
            ['format-letter-spacing', pathIcons + 'format-letter-spacing.svg'],

            ['forward', pathIcons + 'forward.svg'],
            ['front', pathIcons + 'front.svg'],
            ['backwards', pathIcons + 'backwards.svg'],
            ['back', pathIcons + 'back.svg'],

            ['proportions-selected', pathIcons + 'proportions-selected.svg'],
            ['proportions-unselected', pathIcons + 'proportions-unselected.svg'],

            ['horizontal', pathIcons + 'horizontal.svg'],
            ['vertical', pathIcons + 'vertical.svg'],

            ['corners', pathIcons + 'corners.svg'],
            ['corner-radius', pathIcons + 'corner-radius.svg'],
            ['rotation', pathIcons + 'rotation.svg'],

            ['hidden', pathIcons + 'hidden.svg'],

            ['stroke-all', pathIcons + 'stroke-all.svg'],
            ['stroke-size', pathIcons + 'stroke-size.svg'],
            ['stroke-left', pathIcons + 'stroke-left.svg'],
            ['stroke-top', pathIcons + 'stroke-top.svg'],
            ['stroke-right', pathIcons + 'stroke-right.svg'],
            ['stroke-bottom', pathIcons + 'stroke-bottom.svg'],

            ['line-solid', pathIcons + 'line-solid.svg'],
            ['line-dot', pathIcons + 'line-dot.svg'],
            ['line-dashed', pathIcons + 'line-dashed.svg'],
            ['line-dash-dot', pathIcons + 'line-dash-dot.svg'],
            ['line-dash-dot-dot', pathIcons + 'line-dash-dot-dot.svg'],

            ['linejoin-bevel', pathIcons + 'linejoin-bevel.svg'],
            ['linejoin-milter', pathIcons + 'linejoin-milter.svg'],
            ['linejoin-round', pathIcons + 'linejoin-round.svg'],

            ['shipyard', pathIcons + 'shipyard.svg'],
            ['close-circle', pathIcons + 'close-circle.svg'],
            ['check', pathIcons + 'check.svg'],
            ['close', pathIcons + 'close.svg'],

            ['text-align-left', pathIcons + 'text-align-left.svg'],
            ['text-align-center', pathIcons + 'text-align-center.svg'],
            ['text-align-right', pathIcons + 'text-align-right.svg'],

            ['library', pathIcons + 'library.svg'],
            ['mimic-default', pathIcons + 'mimic-default.svg'],
            ['tank', pathIcons + 'tank.svg'],
            ['filter', pathIcons + 'filter.svg'],
            ['filter-list', pathIcons + 'filter-list.svg'],
            ['fan', pathIcons + 'fan.svg'],
            ['generator', pathIcons + 'generator.svg'],
            ['main-engine', pathIcons + 'main-engine.svg'],
            ['pipe', pathIcons + 'pipe.svg'],

            ['pump-vertical-right', pathIcons + 'pump/pump-vertical-right.svg'],
            ['pump-vertical-left', pathIcons + 'pump/pump-vertical-left.svg'],
            ['pump-horizontal-right', pathIcons + 'pump/pump-horizontal-right.svg'],
            ['pump-horizontal-left', pathIcons + 'pump/pump-horizontal-left.svg'],

            ['fan-run', pathIcons + 'fan/fan-run.svg'],
            ['fan-stop', pathIcons + 'fan/fan-stop.svg'],

            ['tank-default', pathIcons + 'tank/tank-default.svg'],
            ['tank-auxiliary-air-reserve', pathIcons + 'tank/tank-auxiliary-air-reserve.svg'],
            ['tank-main-air-reserve', pathIcons + 'tank/tank-main-air-reserve.svg'],

            ['digital-close', pathIcons + 'valve/digital-close.svg'],
            ['digital-open', pathIcons + 'valve/digital-open.svg'],
            [
                'valve-3-hori-open-3-way-open-horizontal-no-indicator',
                pathIcons + 'valve/valve-3-hori-open-3-way-open-horizontal-no-indicator.svg',
            ],
            [
                'valve-3-vert-open-3-way-open-vertical-no-indicator',
                pathIcons + 'valve/valve-3-vert-open-3-way-open-vertical-no-indicator.svg',
            ],
            [
                'valve-hori-closed-2-way-closed-horizontal-no-indicator',
                pathIcons + 'valve/valve-hori-closed-2-way-closed-horizontal-no-indicator.svg',
            ],
            [
                'valve-hori-static-2-way-static-horizontal-no-indicator',
                pathIcons + 'valve/valve-hori-static-2-way-static-horizontal-no-indicator.svg',
            ],
            [
                'valve-vert-closed-2-way-closed-vertical-no-indicator',
                pathIcons + 'valve/valve-vert-closed-2-way-closed-vertical-no-indicator.svg',
            ],
            [
                'valve-vert-static-2-way-static-vertical-no-indicator',
                pathIcons + 'valve/valve-vert-static-2-way-static-vertical-no-indicator.svg',
            ],

            ['switch-on', pathIcons + 'switch/switch-on.svg'],
            ['switch-off', pathIcons + 'switch/switch-off.svg'],

            ['corner-corner-left-down', pathIcons + 'connector/corner-corner-left-down.svg'],
            ['corner-corner-left-up', pathIcons + 'connector/corner-corner-left-up.svg'],
            ['corner-corner-right-down', pathIcons + 'connector/corner-corner-right-down.svg'],
            ['corner-corner-right-up', pathIcons + 'connector/corner-corner-right-up.svg'],
            ['corner-icon', pathIcons + 'connector/corner-icon.svg'],
            ['cross-or-3-way-3-way-down', pathIcons + 'connector/cross-or-3-way-3-way-down.svg'],
            ['cross-or-3-way-3-way-left', pathIcons + 'connector/cross-or-3-way-3-way-left.svg'],
            ['cross-or-3-way-3-way-right', pathIcons + 'connector/cross-or-3-way-3-way-right.svg'],
            ['cross-or-3-way-3-way-up', pathIcons + 'connector/cross-or-3-way-3-way-up.svg'],
            ['cross-or-3-way-cross', pathIcons + 'connector/cross-or-3-way-cross.svg'],
            ['cross-icon', pathIcons + 'connector/cross-icon.svg'],
            ['direction-direction-3', pathIcons + 'connector/direction-direction-3.svg'],
            ['direction-down', pathIcons + 'connector/direction-down.svg'],
            ['direction-left', pathIcons + 'connector/direction-left.svg'],
            ['direction-right', pathIcons + 'connector/direction-right.svg'],
            ['direction-icon', pathIcons + 'connector/direction-icon.svg'],
            ['end-point-down', pathIcons + 'connector/end-point-down.svg'],
            ['end-point-left', pathIcons + 'connector/end-point-left.svg'],
            ['end-point-right', pathIcons + 'connector/end-point-right.svg'],
            ['end-point-up', pathIcons + 'connector/end-point-up.svg'],
            ['end-point-icon', pathIcons + 'connector/end-point-icon.svg'],
            ['lines-horizontal', pathIcons + 'connector/lines-horizontal.svg'],
            ['lines-vertical', pathIcons + 'connector/lines-vertical.svg'],
            ['line-icon', pathIcons + 'connector/line-icon.svg'],

            ['desktop_windows', pathIcons + 'desktop_windows.svg'],
            ['avg-pace', pathIcons + 'avg-pace.svg'],
            ['auto', pathIcons + 'auto.svg'],
            ['no-file', pathIcons + 'no-sim.svg'],

            // repose group
            ['exceed', pathIcons + 'exceed.svg'],
            ['fall', pathIcons + 'fall.svg'],

            // system setting
            ['chevron-right', pathIcons + 'chevron-right.svg'],
            ['location', pathIcons + 'location.svg'],
            ['alarm', pathIcons + 'alarm.svg'],

            // ops setting
            ['arrow-circle-left', pathIcons + 'arrow-circle-left.svg'],
            ['arrow-circle-right', pathIcons + 'arrow-circle-right.svg'],
            ['dp-more-vert', pathIcons + 'dp-more-vert.svg'],

            //mimic editor
            ['sort-asc', pathIcons + 'sort-asc.svg'],
            ['sort-desc', pathIcons + 'sort-desc.svg'],
            ['convert-pdf', pathIcons + 'convert-pdf.svg'],
        ];

        for (let i = 0; i < icons.length; i++) {
            iconRegistry.addSvgIcon(icons[i][0], domSanitizer.bypassSecurityTrustResourceUrl(icons[i][1]));
        }
    }

    ngOnInit() {
        this.loadingSubscription = this.authService.loadingPage$.subscribe((loading: boolean) => {
            this.pagesAreLoading = loading;
        });

        this.isIframe = window !== window.parent && !window.opener;
        this.showLandingPage = false;
        this.canDisplay = true;

        this.languageService.initializeLanguage();

        console.log(`iCreate v${environment.version as string}`);
    }

    ngAfterViewInit() {
        try {
            this.settingsService.init();
            this.themeservice.setTheme();
        } catch (err) {
            console.error(err);
        }
    }

    ngOnDestroy() {
        this.loadingSubscription?.unsubscribe();
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    isHidden() {
        const list = ['', '/lab', '/home'],
            route = this.location.path();
        return list.indexOf(route) > -1;
    }

    getClass() {
        const route = this.location.path();
        if (route.startsWith('/view')) {
            return 'work-void';
        }
        return this.isHidden() ? 'work-home' : 'work-editor';
    }

    async login() {
        if (this.env === RuntimeEnvironments.CLOUD || this.env == RuntimeEnvironments.LOCAL) {
            this.authService.azureLogIn();
        } else {
            this.showLandingPage = false;
            await this.router.navigate(['login']);
        }
    }

    logout() {
        if (this.env === RuntimeEnvironments.CLOUD || this.env == RuntimeEnvironments.LOCAL) {
            this.authService.azureLogOut();
        } else {
            this.authService.edgeLogout();
        }
    }

    openContactForm = () => {
        const dialogRef = this.dialog.open(ContactFormComponent, {
            height: '700px',
            width: '500px',
        });
        dialogRef.afterClosed().subscribe(res => {
            if (res) this.contactService.postInquiry(res);
        });
    };
}
